import { useLocation, Navigate } from 'react-router-dom';
import { useAppAuth } from '@/contexts/AppAuthContext';

/**
 * Guard for dashboard pages. While `user === undefined` the bootstrap is in
 * flight and we render nothing rather than redirect (otherwise a logged-in
 * user briefly bounces to /login on every refresh).
 *
 * Props:
 *   - page: optional page slug; if provided, requires `hasPage(slug)`.
 *   - admin: when true, requires admin role.
 */
export function RequireAppAuth({ children, page, admin: needsAdmin }) {
  const { user, isAdmin, hasPage } = useAppAuth();
  const location = useLocation();

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Loading...</div>;
  }
  if (!user || !user.app_role) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/portal-signin?next=${next}`} replace />;
  }
  if (needsAdmin && !isAdmin) {
    return <NotAllowed message="This area is admin-only." />;
  }
  if (page && !hasPage(page)) {
    return <NotAllowed message={`You don't have access to "${page}". Ask your admin to grant it.`} />;
  }
  return children;
}

function NotAllowed({ message }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6" data-testid="page-access-denied">
      <p className="text-5xl mb-4">🔒</p>
      <p className="text-xl font-semibold text-gray-900">Access denied</p>
      <p className="text-sm text-gray-500 mt-1.5 max-w-sm">{message}</p>
    </div>
  );
}
